import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus, Tag, Ban, CheckCircle } from 'lucide-react';

import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth, API } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';

const CategoriesTab = () => {
  const { getAuthHeaders } = useAuth();
  const { hasPermission } = usePermissions();
  const [categories, setCategories] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    print_station: 'kitchen'
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: getAuthHeaders()
      });
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Error al cargar categorías');
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        icon: category.icon || '',
        print_station: category.print_station || 'kitchen'
      });
    } else {
      setEditingCategory(null);
      setFormData({ 
        name: '', 
        description: '', 
        icon: '',
        print_station: 'kitchen'
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      if (editingCategory) {
        // Update
        await axios.put(
          `${API}/categories/${editingCategory.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Categoría actualizada exitosamente');
      } else {
        // Create
        await axios.post(
          `${API}/categories`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Categoría creada exitosamente');
      }

      setShowDialog(false);
      loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar categoría');
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`¿Estás seguro de eliminar la categoría "${category.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/categories/${category.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Categoría eliminada exitosamente');
      loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar categoría');
    }
  };

  const handleToggleActive = async (category) => {
    try {
      await axios.patch(
        `${API}/categories/${category.id}/toggle-active`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Categoría ${category.is_active !== false ? 'deshabilitada' : 'habilitada'}`);
      loadCategories();
    } catch {
      toast.error('Error al cambiar estado de la categoría');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Categorías</h2>
          <p className="text-gray-600">Gestiona las categorías de productos</p>
        </div>
        {/* Botón Nueva Categoría - Solo si tiene permiso */}
        {hasPermission('categories:create') && (
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Categoría
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <Card
            key={category.id}
            className={`p-6 bg-white/80 backdrop-blur-xl border-orange-100 hover-lift ${
              category.is_active === false ? 'opacity-60 border-gray-300' : ''
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  category.is_active !== false
                    ? 'bg-gradient-to-br from-orange-500 to-red-500'
                    : 'bg-gray-300'
                }`}>
                  {category.icon ? (
                    <span className="text-2xl">{category.icon}</span>
                  ) : (
                    <Tag className="w-6 h-6 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">{category.name}</h3>
                  {category.description && (
                    <p className="text-sm text-gray-600">{category.description}</p>
                  )}
                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full ${
                    category.is_active !== false
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {category.is_active !== false ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              {/* Botón Habilitar/Deshabilitar */}
              <Button
                onClick={() => handleToggleActive(category)}
                variant="outline"
                size="sm"
                className={`flex-1 rounded-xl ${
                  category.is_active !== false
                    ? 'border-yellow-300 text-yellow-700 hover:bg-yellow-50'
                    : 'border-green-300 text-green-700 hover:bg-green-50'
                }`}
              >
                {category.is_active !== false ? (
                  <><Ban className="w-4 h-4 mr-1" /> Deshabilitar</>
                ) : (
                  <><CheckCircle className="w-4 h-4 mr-1" /> Habilitar</>
                )}
              </Button>
              {/* Botón Editar - Solo si tiene permiso */}
              {hasPermission('categories:edit') && (
                <Button
                  onClick={() => handleOpenDialog(category)}
                  variant="outline"
                  size="sm"
                  className="flex-1 border-orange-200 hover:bg-orange-50 rounded-xl"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              )}
              
              {/* Botón Eliminar - Solo si tiene permiso */}
              {hasPermission('categories:delete') && (
                <Button
                  onClick={() => handleDelete(category)}
                  variant="outline"
                  size="sm"
                  className="flex-1 border-red-200 hover:bg-red-50 text-red-600 rounded-xl"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {categories.length === 0 && (
        <div className="text-center py-12">
          <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No hay categorías creadas</p>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
            </DialogTitle>
            <DialogDescription>
              {editingCategory 
                ? 'Modifica los datos de la categoría existente' 
                : 'Crea una nueva categoría para agrupar productos'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Bebidas, Entradas, Platos Fuertes"
                className="rounded-xl"
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Descripción</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción de la categoría"
                className="rounded-xl"
              />
            </div>

            <div>
              <Label htmlFor="icon">Icono (Emoji)</Label>
              <Input
                id="icon"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder="Ej: 🍔 🍕 🥤 🍰 🍜"
                className="rounded-xl"
                maxLength={10}
              />
              <p className="text-xs text-gray-500 mt-1">Puedes usar hasta 10 caracteres (múltiples emojis)</p>
            </div>

            <div>
              <Label htmlFor="print_station">Estación de Impresión</Label>
              <Select
                value={formData.print_station}
                onValueChange={(value) => setFormData({ ...formData, print_station: value })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kitchen">Cocina</SelectItem>
                  <SelectItem value="bar">Bar</SelectItem>
                  <SelectItem value="desserts">Postres</SelectItem>
                  <SelectItem value="grill">Parrilla</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-xl"
              >
                {editingCategory ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoriesTab;
